/**
 * Search/command-ranking helpers used by the global nav palette and any
 * future command-palette-style surface.
 *
 * These were extracted from the GlobalNavPalette so the underlying
 * algorithm — score-by-match-position + group-by-category + cap-per-bucket
 * — can be reused for any list of categorized, label-bearing items.
 *
 * Pure: no React, no stores, no services. All three operate on plain data
 * and return new collections.
 */

/**
 * Minimum shape required by {@link rankCommands}.
 *
 * `label` is the primary user-visible string (matched with full priority).
 * `searchText` is a precomputed lowercase haystack — usually the label plus
 * keywords/synonyms — used for word-prefix and substring fallback matches.
 */
export interface Rankable {
  label: string;
  searchText: string;
}

/**
 * Filter + rank a list of rankable items by relevance to `query`, dropping
 * items that don't match anywhere.
 *
 * Buckets (lower score = better match):
 *   0 — exact label match
 *   1 — label starts with query
 *   2 — any word in `searchText` starts with query
 *   3 — query appears anywhere in `searchText`
 *
 * Within a bucket, original order is preserved (stable). When the query is
 * empty or whitespace-only, the original list is returned unchanged.
 */
export function rankCommands<T extends Rankable>(commands: T[], query: string): T[] {
  const q = query.trim().toLowerCase();
  if (!q) return commands;

  const scored: Array<{ cmd: T; score: number; index: number }> = [];

  commands.forEach((cmd, index) => {
    const label = cmd.label.toLowerCase();
    const text = cmd.searchText;
    let score: number | null = null;

    if (label === q) score = 0;
    else if (label.startsWith(q)) score = 1;
    else if (text.split(/\s+/).some((w) => w.startsWith(q))) score = 2;
    else if (text.includes(q)) score = 3;

    if (score !== null) scored.push({ cmd, score, index });
  });

  scored.sort((a, b) => a.score - b.score || a.index - b.index);
  return scored.map((s) => s.cmd);
}

/**
 * Group items by `category`, preserving `categoryOrder`.
 *
 * Empty categories are dropped from the output. Items whose category is not
 * listed in `categoryOrder` are silently skipped (caller bug, but we don't
 * want to throw inside a render path).
 *
 * Generic over the category key so the same helper works for the nav
 * palette's category union and any future enum-shaped grouping.
 */
export function groupByCategory<T extends { category: K }, K extends string>(
  commands: T[],
  categoryOrder: readonly K[],
): Map<K, T[]> {
  const grouped = new Map<K, T[]>();
  for (const cat of categoryOrder) grouped.set(cat, []);
  for (const cmd of commands) {
    const bucket = grouped.get(cmd.category);
    if (bucket) bucket.push(cmd);
  }
  for (const [cat, list] of grouped) {
    if (list.length === 0) grouped.delete(cat);
  }
  return grouped;
}

/**
 * Cap the number of items shown per category. Used when no query has been
 * typed yet — keeps the resting palette compact instead of unrolling every
 * workflow / app / project on the user's account.
 *
 * `overrides` lets callers raise the cap for categories whose list is
 * statically bounded (e.g. static `pages` routes), where truncation offers
 * no value.
 *
 * Returns `{ items, truncated }` per category so callers can render a
 * "+N more" affordance.
 */
export function capPerCategory<T, K extends string>(
  grouped: Map<K, T[]>,
  max: number,
  overrides?: Partial<Record<K, number>>,
): Map<K, { items: T[]; truncated: number }> {
  const capped = new Map<K, { items: T[]; truncated: number }>();
  for (const [cat, list] of grouped) {
    const limit = overrides?.[cat] ?? max;
    if (list.length <= limit) {
      capped.set(cat, { items: list, truncated: 0 });
    } else {
      capped.set(cat, { items: list.slice(0, limit), truncated: list.length - limit });
    }
  }
  return capped;
}
